import {z} from 'zod'

import {CLASSROOM_LEVEL_GRADES, levelAgreesWithGrade} from '#/lib/curriculum'

/**
 * A classroom is a place a school gathers people around a level of study.
 *
 * One kind, one shape: a small, shared profile with a cover, a name, a bio,
 * members and posts. It briefly came in a second, "virtual · paid" kind, which
 * was a label and a Subscribe button with no billing behind it; the label is
 * gone, and a classroom that carried it is an ordinary classroom.
 */
export const classroomVisibilitySchema = z.enum(['public', 'private'])
export type ClassroomVisibility = z.infer<typeof classroomVisibilitySchema>

/**
 * Who a post written in a classroom is for.
 *
 * A room has a visibility of its own — public or private — and that is the
 * room's answer to *may a stranger read this at all*. This is the *author's*
 * answer, asked of one post: a teacher may write a public room's post for the
 * room's members alone, or for its teachers alone, and a public room is exactly
 * where that question has more than one answer. A course of ten announcements
 * to the world and one exam paper for the people who sit it are both the same
 * room's posts, and only the author knows which is which.
 *
 * It is a **list of seats**, and nothing else is the world: no seat named means
 * anybody may read it, one seat is that seat's people, and all three is the whole
 * room. The seats are the room's own three — teacher, student, parent — and the
 * school above them is in every list (`audienceIncludes`), so asking *who is this
 * for* with the room's own words means a teacher reading the choice already knows
 * what *students* means here.
 *
 * A list rather than a choice because an author addresses two kinds of people at
 * once: the parents *and* the students are who a note about a trip home is for,
 * and a form with one answer in it makes that note impossible to write. The two
 * *everyone* answers are not lists at all — the world is the empty list and the
 * whole room is all three seats — which is what keeps them a single choice
 * (`PostAudienceOptions` draws them as the two radios) while the seats below
 * them combine.
 *
 * The order is always the room's own (`CLASSROOM_POST_SEATS`), so two ways of
 * saying the same audience are the same list and can be compared (`sameAudience`)
 * — and one list can be written down, read back and asked about
 * (`audienceIncludes`) without ever being re-sorted.
 *
 * What it is *not* is a lock. AT Protocol has no way to make a record private,
 * and this says a second time what `CLASSROOM_ONLY_POST` already says about work
 * made for a room: the app does not put it in a feed, on a profile or in front
 * of anybody the post was not addressed to, and neither does anything else built
 * on this fork. The one thing it can promise is that it will not *publish* such
 * a post by accident — which is why a post written for less than everyone is
 * written as the room's own entry rather than the room's public work list.
 */
export const classroomPostSeatSchema = z.enum([
  'teachers',
  'parents',
  'students',
])
export type ClassroomPostSeat = z.infer<typeof classroomPostSeatSchema>

/** The room's own three seats, in the order the room lists them. */
export const CLASSROOM_POST_SEATS = ['teachers', 'parents', 'students'] as const

/**
 * Who a post is for, as the app holds it: seats in `CLASSROOM_POST_SEATS` order.
 *
 * Empty is the world and all three are the whole room, and those are the only
 * two answers every seat shares — which is why the screen offers them as choices
 * of their own (`EVERYONE_MAY_READ`, `THE_WHOLE_CLASSROOM`) rather than as a
 * list that happens to be empty or full.
 */
export type ClassroomPostAudience = ClassroomPostSeat[]

/** Nobody in particular was named: anybody may read it. */
export const EVERYONE_MAY_READ: ClassroomPostAudience = []

/** Everybody in the room, and nobody outside it. */
export const THE_WHOLE_CLASSROOM: ClassroomPostAudience = [
  ...CLASSROOM_POST_SEATS,
]

function isPostSeat(value: unknown): value is ClassroomPostSeat {
  return (
    typeof value === 'string' &&
    (CLASSROOM_POST_SEATS as readonly string[]).includes(value)
  )
}

/**
 * An audience as a record, or this device, wrote it — read as the app's own list.
 *
 * Two shapes are read, and the older one has to be: the first build of this
 * question asked *who is this for* with one answer, so a post written by it — or
 * an answer it remembered on this device — says `public`, `members` or the name of
 * a single seat. `members` is exactly the whole room and `public` is exactly the
 * empty list, so nothing is lost by reading them as what they meant, and every
 * post ever addressed keeps the audience it was written for.
 *
 * Anything else — a word this app has never said, a list with a stranger in it —
 * is `undefined`, which is what the schema below turns back into a parse failure:
 * an audience nobody can read is not an audience, and a post that carries one must
 * not be read as if it had named nobody (`postAudience`).
 */
export function readPostAudience(
  value: unknown,
): ClassroomPostAudience | undefined {
  if (value === undefined || value === null) return undefined
  if (typeof value === 'string') {
    if (value === 'public') return []
    if (value === 'members') return [...CLASSROOM_POST_SEATS]
    return isPostSeat(value) ? [value] : undefined
  }
  if (Array.isArray(value)) {
    if (!value.every(isPostSeat)) return undefined
    // Deduplicated and put in the room's order: one audience, one list.
    return CLASSROOM_POST_SEATS.filter(seat => value.includes(seat))
  }
  return undefined
}

/**
 * The field a record, or the device, keeps an audience in.
 *
 * Normalizing here rather than at each reading is what lets everything past this
 * boundary hold an audience in one shape: a mark read off the network and a mark
 * written by this build are the same list, and the older single-answer words are
 * converted once, where the record is read, instead of in every rule that asks
 * who a post is for.
 */
export const classroomPostAudienceSchema = z.preprocess(
  value => readPostAudience(value) ?? value,
  z.array(classroomPostSeatSchema),
)

/**
 * Who a post in a room is for when nobody has said.
 *
 * Which is what a room's visibility already means: a public room's post is
 * everyone's, and a private room's post is the room's — there is nothing else it
 * *could* be, because the room itself is not everybody's to read. This is the
 * answer the composer starts a post with, and the answer every post written
 * before this question existed still reads as.
 */
export function defaultPostAudience(
  visibility: ClassroomVisibility,
): ClassroomPostAudience {
  return visibility === 'private' ? [...CLASSROOM_POST_SEATS] : []
}

/**
 * The audience a new post in a room starts on, given a remembered answer.
 *
 * A remembered *everyone* is not honoured in a private room: a post there goes
 * to the world only when the author picks *Public* in the composer they are
 * writing in, never because of an answer kept from long ago (an older build
 * kept `[]` for private rooms, and those posts are often minors').
 */
export function startingPostAudience(
  visibility: ClassroomVisibility,
  saved: ClassroomPostAudience | undefined,
): ClassroomPostAudience {
  if (!saved) return defaultPostAudience(visibility)
  if (visibility === 'private' && saved.length === 0) {
    return defaultPostAudience(visibility)
  }
  return saved
}

/**
 * The room entry's `visibility` for a post written with this audience.
 *
 * The author picks it per post in the composer (spec 11.3): nobody named is
 * *Public*, and anything else - the whole room or some of its seats - is *Only
 * this classroom*, with the seats narrowing it as the entry's `audience`.
 */
export function postVisibility(
  audience: ClassroomPostAudience,
): 'public' | 'members' {
  return mayAnyoneRead(audience) ? 'public' : 'members'
}

/**
 * Whether an audience is the world's — nobody in particular was named.
 *
 * The one question four different readings ask of a list, and it is asked here so
 * that *who is this for* has a single answer in the app: `[]` is the world, and
 * everything else names somebody. Spelled as a function rather than left as
 * `.length === 0` at each site because *is this everybody's* is what the readers
 * are actually asking, and the two are not the same question once the app starts
 * drawing an empty list as *Public post*.
 */
export function mayAnyoneRead(audience: ClassroomPostAudience): boolean {
  return audience.length === 0
}

/** Whether an audience is the room's every seat — the whole classroom. */
export function isTheWholeClassroom(audience: ClassroomPostAudience): boolean {
  return audience.length === CLASSROOM_POST_SEATS.length
}

/**
 * Whether two audiences say the same thing.
 *
 * By membership rather than by position, because the two lists being compared
 * need not have been written down by the same code: the composer compares the
 * audience it is about to publish with the one it started from, and one of those
 * may have come off a record another client wrote in another order. Two audiences
 * that name the same seats are the same answer, which is the question the
 * composer is asking — *has the author changed it?* — and the order is not part of
 * the answer.
 */
export function sameAudience(
  a: ClassroomPostAudience,
  b: ClassroomPostAudience,
): boolean {
  return a.length === b.length && a.every(seat => b.includes(seat))
}

/**
 * Who a mark says its post is for, with the default filled in.
 *
 * Read off the *mark* rather than off the room, because the mark is the half
 * that travels: a post's audience is the author's choice, kept where the post
 * is, and a reader who has never seen the room still asks the same question of
 * the same record (`readClassroomPostMark`).
 */
export function postAudience(mark: {
  audience?: ClassroomPostAudience
  visibility: ClassroomVisibility
}): ClassroomPostAudience {
  return mark.audience ?? defaultPostAudience(mark.visibility)
}

/**
 * Whether a post addressed to an audience reaches a reader holding a seat.
 *
 * The school is in every audience: it runs the room, it hands out the code, it
 * answers the asks and it can end the room — a school that could not read what
 * it administers would be a school reading a room it cannot maintain. Every
 * other seat is read exactly as it is named.
 */
export function audienceIncludes(
  audience: ClassroomPostAudience,
  viewer: {role: MemberRole | null},
): boolean {
  if (mayAnyoneRead(audience)) return true
  const role = viewer.role
  if (!role) return false
  if (role === 'admin') return true
  return audience.includes(seatOf(role))
}

/**
 * The seat a member's role is one of.
 *
 * The school's own seat has none — it is in every audience by the rule above,
 * because it runs the room rather than sitting in it — and every other role is
 * exactly one of the room's three seats, in the plural the seats are named in.
 */
function seatOf(role: 'teacher' | 'student' | 'parent'): ClassroomPostSeat {
  switch (role) {
    case 'teacher':
      return 'teachers'
    case 'student':
      return 'students'
    case 'parent':
      return 'parents'
  }
}

/**
 * The seat an account holds in a classroom, as this device knows it.
 *
 * `null` is not *unknown*: it means this account holds no seat here, which is
 * the answer for a stranger — and for everybody, on a device that has never
 * heard of the room. The school's own seat is named rather than left to the
 * member list, because the room's record writes it as a member of a different
 * kind (`isAdminMember`).
 */
export function classroomMemberRole(
  classroom: {
    members: {did: string; role: MemberRole}[]
    ownerDid: string
  },
  actor: {did: string} | undefined,
): MemberRole | null {
  const did = actor?.did
  if (!did) return null
  if (did === classroom.ownerDid) return 'admin'
  return classroom.members.find(m => m.did === did)?.role ?? null
}

/**
 * How a classroom names its level.
 *
 * `STANDARD` uses the shared list of levels everyone can filter by. `SYSTEM`
 * lets a private school name its own — an internal "System 3" that means
 * something only inside that school. Private schools exist precisely so their
 * naming does not have to line up with anybody else's.
 */
export const levelNameTypeSchema = z.enum(['STANDARD', 'SYSTEM'])
export type LevelNameType = z.infer<typeof levelNameTypeSchema>

/**
 * The seat a person holds in a classroom.
 *
 * `admin` is the school that runs the room — the account that created it. It is
 * deliberately not a teacher: the school owns the classroom, hands out the
 * code, answers the asks and takes people out, and counting it among the
 * teachers would make a room with no teachers look like it had one. Only the
 * owner ever holds this seat; a school joining somebody else's room is a
 * teacher there.
 */
export const memberRoleSchema = z.enum([
  'admin',
  'teacher',
  'student',
  'parent',
])
export type MemberRole = z.infer<typeof memberRoleSchema>

/** A seat a visitor can hold — everything except the school's own. */
export const joinableRoleSchema = z.enum(['teacher', 'student', 'parent'])
export type JoinableRole = z.infer<typeof joinableRoleSchema>

export const classroomMemberSchema = z.object({
  did: z.string(),
  handle: z.string(),
  displayName: z.string().optional(),
  avatar: z.string().optional(),
  role: memberRoleSchema,
  joinedAt: z.string(),
  /** Whether the school named this member a moderator (server-held). */
  isModerator: z.boolean().optional(),
  /**
   * The seat a parent takes when their child joins and the app has no linked
   * parent account to name. The classroom still counts them as a parent — the
   * count exists to describe the room, not to inventory verified accounts.
   */
  placeholder: z.boolean().optional(),
  /**
   * For a parent seat: the student whose joining put them here. A parent is in
   * the room because their child is, so the child leaving takes the seat away.
   */
  forStudentDid: z.string().optional(),
  /**
   * The key this seat is filed under in the school's repo, when the seat was
   * granted across devices.
   *
   * The school's device keeps the token it wrote the seat under so that taking the
   * person out of the room takes the record away with them — a member removed on
   * the school's phone must not stay a member on the phone in their pocket. Absent
   * means the seat never left this device.
   */
  remoteToken: z.string().optional(),
})
export type ClassroomMember = z.infer<typeof classroomMemberSchema>

export const joinRequestSchema = z.object({
  did: z.string(),
  handle: z.string(),
  displayName: z.string().optional(),
  avatar: z.string().optional(),
  role: joinableRoleSchema,
  requestedAt: z.string(),
  /** Why the school is being asked, in the asker's own words. */
  message: z.string().optional(),
  /**
   * The asker's own meeting point, for an ask that crosses devices.
   *
   * An ask made on the school's own device stays there and needs no address. An
   * ask made *anywhere else* has to leave and come back, and what comes back is a
   * seat — so the asker draws a token (`makeAskToken`) and keeps it here, the ask
   * carries it to the school's repo, and the school files the seat under it. The
   * token names nobody, which is the point: the record that says *somebody was let
   * in* is filed under a word only two devices know.
   */
  token: z.string().optional(),
  /**
   * Whether this ask was written into the school's own repo.
   *
   * An ask made on the school's device is already where the school reads; an ask
   * made anywhere else had to be sent (`askAcrossDevices`), and only the sent one
   * has an answer to come back. The flag is what keeps a device from looking for a
   * record that was never written, and what keeps a local ask from being answered
   * twice.
   */
  acrossDevices: z.boolean().optional(),
})
export type JoinRequest = z.infer<typeof joinRequestSchema>

/**
 * Who may say yes to a kind of ask.
 *
 * A classroom has a door and somebody standing at it. `'all-teachers'` is every
 * teacher seat in the room — the answer a school gets by default, because a
 * school that has teachers trusts them with its door — and a list of dids is
 * *only these people*, which is what a school chooses when it wants one or two
 * of its teachers to decide. Absent is `'all-teachers'` (`classroomApproversFor`).
 *
 * The school itself is always at the door whatever this says, and so is a
 * moderator it named: the setting decides who *else* may answer.
 */
export const classroomApproversSchema = z.union([
  z.literal('all-teachers'),
  z.array(z.string()),
])
export type ClassroomApprovers = z.infer<typeof classroomApproversSchema>

/** The value of `classroomApproversSchema` that hands the door to every teacher. */
export const ALL_TEACHERS = 'all-teachers'

/**
 * One level change, as it happened.
 *
 * Both sides are written down rather than one: `from` is the period that ends
 * (what the work already in the room was written at) and `to` is where the room
 * went. Reading the pair off the room's current level instead would rewrite
 * every past move the moment the room moved again.
 */
export const classroomLevelMoveSchema = z.object({
  from: z.string(),
  fromGrade: z.string().optional(),
  to: z.string(),
  toGrade: z.string().optional(),
  at: z.string(),
})
export type ClassroomLevelMove = z.infer<typeof classroomLevelMoveSchema>

export const classroomSchema = z.object({
  id: z.string(),
  /** URL-safe slug, unique per classroom. */
  handle: z.string(),
  name: z.string(),
  /** The bio. A small piece of text describing the classroom. */
  description: z.string(),
  /**
   * Cover photo: the picture's own bytes, as the device keeps them, drawn in the
   * same 3:1 band a profile banner uses, with nothing on top of it.
   *
   * Kept here rather than addressed, because there is nowhere to address it *from*:
   * a classroom is device-local, and so is its picture
   * (`#/lib/media/coverPicture`).
   */
  cover: z.string().optional(),
  /** Cover address served by the classroom server; set instead of `cover` bytes. */
  coverUrl: z.string().optional(),
  visibility: classroomVisibilitySchema,
  /**
   * The level the classroom teaches — one of `STANDARD_LEVELS`, a private
   * school's own SYSTEM name, or **empty**: a classroom with no level is open to
   * every learner, whatever they are studying.
   */
  level: z.string(),
  /**
   * The year inside the level, when the level has years.
   *
   * A grade is a *code* from the app's school vocabulary (`Level_A1` is 1st year
   * of primary school, `Level_S4` the BAC year, `KG2` a kindergarten year) — the
   * same codes a capsule carries, so the room's year and its work's year are one
   * fact rather than two that have to be kept in step (see
   * `CLASSROOM_LEVEL_GRADES`). Absent on every classroom whose level has no years
   * (High School, University, Other) and on every classroom made before grades
   * existed, which is read as *the whole level*, not as a missing answer.
   */
  grade: z.string().optional(),
  /**
   * The school's own name for its level, printed in place of the shared name.
   *
   * `University` and `Other` are the two levels that are not a country's own
   * school years, so a room at either of them is usually one particular thing:
   * a faculty, a course, a centre. The shared name stays on the record — the
   * gate, the filters and a level change all read it — and this is what the
   * room *says* instead: `Faculty of Engineering` rather than `University`.
   * Empty means the shared name, which is what every room did before the field.
   */
  levelLabel: z.string().max(60).optional(),
  /**
   * The country the classroom takes its learners from, as the school's own
   * account records it (an ISO code, `TN`).
   *
   * A classroom is a country's school: a learner of another country is not its
   * audience (`learnerMayJoinClassroom`), which is a rule the school cannot
   * enforce by itself — it does not know who is out there — so the room carries
   * the country and the door reads it. Taken from the school at creation and
   * not editable: it is a fact about the school, not a choice about the room.
   * Absent on every classroom made before the field, and on a school whose
   * country has never been answered: absent refuses nobody.
   */
  country: z.string().max(4).optional(),
  /**
   * Whether the classroom takes the learners of every country.
   *
   * The answer for a University or an `Other` room that is not *for* one
   * country's students — a faculty with foreign students, a course anybody may
   * take. It is a choice the school makes out loud, because it is about who the
   * room is for: an international classroom is a different audience, not a
   * better one, and the form says so. Absent means the room takes its own
   * country's learners, which is what every classroom did before the field.
   */
  international: z.boolean().optional(),
  /**
   * When the room's level (or its year) last changed.
   *
   * A classroom changes what it teaches slowly: the level is a promise to every
   * member it has — they are told to move when it moves (the server
   * writes that announcement) — and a promise rewritten every week is not one.
   * The stamp is written only when the level *actually* changes, so a room can be
   * corrected the day it is made: a mistake is the reason this field exists.
   *
   * Absent on every classroom made before it, which reads as *the level may
   * change now*, not as a change that just happened (see `levelChangeLockedUntil`).
   */
  levelChangedAt: z.string().optional(),
  levelNameType: levelNameTypeSchema,
  /**
   * Whether parents may take a seat in this classroom.
   *
   * `true` — and *absent*, which is how every classroom made before the rule
   * reads — means parents may join; only a creator who chose to keep parents
   * out at creation leaves an explicit `false`. It cannot be turned off for a
   * level that guarantees parents, and it cannot be changed in an edit while a
   * parent holds a seat (see `parentsMayJoin` and the store).
   */
  parentsCanJoin: z.boolean().optional(),
  /**
   * Whether a valid invitation code seats the person at once (the server's
   * default) or still sends their ask to the people who answer the door.
   */
  autoAcceptWithCode: z.boolean().optional(),
  /**
   * Who may accept a *student* asking to join, beside the school itself.
   * Absent reads as `'all-teachers'` — the door every classroom already had.
   */
  studentApprovers: classroomApproversSchema.optional(),
  /**
   * Who may accept a *parent* asking to join, beside the school itself.
   * Absent reads as `'all-teachers'`, for the same reason.
   */
  parentApprovers: classroomApproversSchema.optional(),
  /**
   * The seats the school has handed the running of the room to.
   *
   * A moderator does the school's work — answers requests, invites, changes the
   * rules beside it (`canManageClassroom`) — with one exception: they cannot
   * delete the classroom (`canDeleteClassroom`). That one act cannot be taken
   * back, and a room is the school's own.
   *
   * Only the school names them, and it names them behind its own password
   * (`#/screens/Classroom/components/ClassroomSettingsDialog`), because handing
   * away the door is not a tap. Absent means none.
   */
  moderators: z.array(z.string()).optional(),
  interests: z.array(z.string()),
  ownerDid: z.string(),
  ownerHandle: z.string(),
  ownerName: z.string().optional(),
  /**
   * The kind of account that made the classroom, as onboarding recorded it.
   *
   * A classroom is a school's room, so a learner cannot make one, and a room a
   * learner made — through an earlier build, or an account that has since been
   * read as one — is not one the app treats as a school's. The owner's own
   * record cannot answer that question for every reader, which is why the answer
   * is written down here, at creation.
   *
   * Absent on every classroom made before the field existed, and absent when the
   * account's type was unknown at the time. Absent is not a claim of anything:
   * the purge reads a room with no `ownerAccountType` as learner-owned only when
   * the signed-in learner is the account that owns it.
   */
  ownerAccountType: z.string().optional(),
  /**
   * The school's code, on the rooms that have one.
   *
   * A code **never takes a seat**: both doors it opens send an ask
   * (`joinByCode` → `requestToJoin`), and the school answers it — so a code is
   * not a key, it is the right to knock, and it lives here, on the device that
   * holds the room, rather than in the room's published record. It used to ride
   * in that record, where anybody who knew the school's handle could read it
   * straight off the PDS for a room whose whole promise is that it cannot be
   * found. It now travels the way an invitation does — in the link and the QR the
   * school hands out, which carry the room's card and the card carries the code
   * (`classroomInvitationLink`).
   *
   * A **public** room has no code at all. It can be found, so the way in is the
   * ask, and a second door the room's own page does not offer would be a door the
   * product denies while the school hands it out. Absent is *no code*, which is
   * why an absent code opens nothing (`joinByCode` reads only a code that is
   * there), and why a room read from a link has none until an invitation brings
   * one.
   */
  joinCode: z.string().optional(),
  /**
   * Every move the room's level has made, oldest first.
   *
   * Written when the level moves, beside the cooldown stamp, because the *old*
   * level is the one fact the room cannot read off itself afterwards — and it is
   * the fact the archive needs: the work already in the room keeps the level it
   * was written at (`classroomLevel` on the entry), so a move leaves a period
   * behind, and the divider that hides that period says what the room moved from
   * and to. A room that moves back to a level it once taught keeps the record of
   * having left it: the record is history, and the archive is not.
   */
  levelMoves: z.array(classroomLevelMoveSchema).optional(),
  /**
   * The server's count of the room's seats.
   *
   * The server sends a room without its roster, so this is the number the page
   * says (`roomCounts`); nothing reads it to decide who is in the room.
   */
  publishedCounts: z
    .object({
      teachers: z.number(),
      students: z.number(),
      parents: z.number(),
    })
    .optional(),
  /** The server's count of the work in the room. */
  contentCount: z.number().optional(),
  members: z.array(classroomMemberSchema),
  requests: z.array(joinRequestSchema),
  /**
   * The asks this room answered with *no*, so the asker can be told.
   *
   * A refusal used to be the one answer nobody heard: the request left the room's
   * list, and the person who sent it found the door again with nothing said — the
   * room's silence was indistinguishable from its having not looked yet. No seat
   * is kept (that is the point of refusing), so what is kept is the fact that
   * this did was turned away, and the room's page says so where that person is
   * standing (`ClassroomHeaderActions`).
   *
   * A new ask clears the entry: the room answered the old question, and the next
   * one is a question again rather than a refusal already given. Absent means the
   * room has refused nobody.
   */
  declined: z
    .array(
      z.object({
        did: z.string(),
        handle: z.string().optional(),
        at: z.string(),
      }),
    )
    .optional(),
  createdAt: z.string(),
})
export type Classroom = z.infer<typeof classroomSchema>

/**
 * A classroom's content, and the two ways it can be seen.
 *
 * `public` means the classroom shows it to anyone who can see the classroom;
 * `members` means only the people in the room. A private classroom offers no
 * choice at all — everything in it is for its members.
 */
export const contentVisibilitySchema = z.enum(['public', 'members'])
export type ContentVisibility = z.infer<typeof contentVisibilitySchema>

export const contentKindSchema = z.enum([
  'post',
  'capsule',
  'flashbook',
  'module',
])
export type ContentKind = z.infer<typeof contentKindSchema>

/**
 * One piece of content in a classroom.
 *
 * There is no classroom lexicon, so this is a device-local record. When the
 * same content was published to the author's own profile — the answer to the
 * question the composer asks in a public classroom — `postUri` points at that
 * post, and the entry is a *view* of it rather than a copy.
 *
 * The same shape carries the two kinds of work that are *two records* — a
 * capsule, and a FlashBook. Each is published as a post (`postUri`) with its
 * own record beside it (`capsuleUri`, `flashbookUri`), and both addresses are
 * kept because they answer different questions: the post is what everything
 * that draws work reads, and the record is what says what the work *is* — its
 * title, its level, its subject, its slides or pages, and the cover its card
 * plays. See the two address fields below.
 *
 * An entry with `capsuleUri` and no `postUri` is one written before that post
 * existed — the room has the capsule and nothing to react to (the room draws it
 * from the record, `ClassroomCapsuleCard`). A *book* with no
 * post is not that: it is a draft, which is a state of its own and never reaches
 * a room, so nothing adopts one. Neither is a *module*: the third kind of
 * two-record work, and a module without a post is a course still being
 * assembled.
 */
export const classroomContentSchema = z.object({
  id: z.string(),
  classroomId: z.string(),
  kind: contentKindSchema,
  visibility: contentVisibilitySchema,
  /**
   * Who the author addressed, when the entry is a post written in the room.
   *
   * The entry is the room's own copy of what it holds, and the post's record
   * carries the same answer (`classroomPostMarkSchema`). It is kept here too
   * because the room's own list is read *before* any post is fetched: a teacher
   * whose post was for the students has to not see it, and a room that had to
   * fetch a post to know who may read its own entry would show it first and ask
   * afterwards.
   *
   * Absent on everything written before the question existed, and on every kind
   * of work that does not ask it — a capsule, a book and a module are made for
   * the room as a whole. Absent reads as the room's own default, by the same rule
   * the mark uses (`postAudience`).
   */
  audience: classroomPostAudienceSchema.optional(),
  text: z.string(),
  /** The capsule's own title, when this entry is one. */
  capsuleTitle: z.string().optional(),
  authorDid: z.string(),
  authorHandle: z.string(),
  authorName: z.string().optional(),
  authorAvatar: z.string().optional(),
  /** The post this also is, when it was published to the author's profile. */
  postUri: z.string().optional(),
  postCid: z.string().optional(),
  /**
   * The capsule record this is, when the entry is a capsule at all.
   *
   * A capsule is two records — the capsule and the post it lives on — and the
   * room keeps both addresses because they answer different questions. The post
   * is what everything that draws work reads (see `postUri`); the record is what
   * says what the capsule *is*: its title, its level, its subject, its slides,
   * and the cover film that plays on its card.
   *
   * A capsule made for a room used to arrive with this address and no post, on
   * purpose, because a capsule with no post is in no feed and on no profile. A
   * post cannot be private on AT Protocol, so that was the only way to keep one
   * out of the world — and it cost the room everything a post is for: nothing to
   * like, nothing to reply to, no thread, and no menu. A capsule for a room is
   * now a post that says on its record that it belongs to that room, which is
   * what the app hides it by instead.
   */
  capsuleUri: z.string().optional(),
  capsuleCid: z.string().optional(),
  /** The book's own title, when this entry is one. */
  flashbookTitle: z.string().optional(),
  /**
   * The book record this is, when the entry is a book at all.
   *
   * A book is two records exactly as a capsule is — the post everything draws
   * from, and the `app.bsky.feed.flashbook` record that says what the book is:
   * its title, its theme, its cover, how long it is and how much of it is work.
   * The room keeps both, for the same reason: whoever draws the book needs the
   * record, and whoever reacts to it needs the post.
   *
   * Unlike a capsule there is no third state here. A book with no post is a
   * draft, which is the creator's own unfinished work and has no business in a
   * room, so every book entry has both addresses or none at all.
   */
  flashbookUri: z.string().optional(),
  flashbookCid: z.string().optional(),
  /** The module's own title, when this entry is one. */
  moduleTitle: z.string().optional(),
  /**
   * The module record this is, when the entry is a module at all.
   *
   * A module is the third kind of two-record work: a post, which is what a room
   * reads and reacts to, and an `app.bsky.feed.module` record, which is the
   * playlist itself. A room keeps both for the reason it keeps a capsule's two:
   * the post is what draws in the Posts tab, and the record is what says what the
   * course *is* — its title, its subject, its level, its capsules and their order.
   *
   * A module with no post is a draft, so a room's entry always carries both
   * addresses or neither. Its *capsules* are ordinary capsules with their own
   * posts and their own entries: a module is a way of gathering work, not a
   * second kind of work, and nothing about a capsule changes because a module
   * holds it.
   */
  moduleUri: z.string().optional(),
  moduleCid: z.string().optional(),
  /**
   * Set when the entry is the room announcing a change to itself.
   *
   * A classroom can change the level it teaches (once a month), and the members
   * it already has are **not** removed by that: the ones whose year no longer
   * matches are told, and given the two things they can do about it — change
   * their level, or leave. That telling is an entry in the room's own list, so it
   * is written the moment the level moves and every member reads it where the
   * room's other posts are.
   *
   * The facts it announces are kept beside it (`announcedLevel`, `announcedGrade`)
   * rather than read off the classroom: a room may move again after the cooldown,
   * and an old announcement must still say what it said.
   */
  announcement: z.enum(['level']).optional(),
  /** The level the announcement names, when the entry is one. */
  announcedLevel: z.string().optional(),
  /** The year the announcement names, when the level it names has one. */
  announcedGrade: z.string().optional(),
  /**
   * The level the room taught when this was written into it.
   *
   * A level change does not throw away what the room already holds: the work
   * written at the old level is **archived**, not deleted — hidden behind a
   * divider that says the room moved, and shown again the moment the room comes
   * back to it (`contentFromAnotherLevel`). That rule needs the level each piece
   * of work was written at, and the only moment it can be recorded is the moment
   * the work is written.
   *
   * Absent on every entry written before the field existed, and read as *the
   * level the room teaches now* — a device that updates into this rule must not
   * archive the work it already has.
   */
  classroomLevel: z.string().optional(),
  /** The year the room taught when this was written, when it had one. */
  classroomGrade: z.string().optional(),
  /**
   * The members who have answered the announcement.
   *
   * An announcement carries the two answers the room exists to offer — move with
   * it, or leave — and *keep* is the third: a teacher or a parent who is told the
   * level moved and wants neither. Being asked once is the whole of it, so the
   * answer is written down and the buttons are not drawn again for the person who
   * gave it.
   */
  acknowledged: z.array(z.string()).optional(),
  /** Whether the server lets this reader take the entry out of the room. */
  viewerCanManage: z.boolean().optional(),
  createdAt: z.string(),
})
export type ClassroomContent = z.infer<typeof classroomContentSchema>

/** What a caller hands over to add a piece of work to a classroom. */
export interface AddContentInput {
  classroomId: string
  kind: ContentKind
  visibility: ContentVisibility
  /** Who in the room the entry is for, when the author narrowed it. */
  audience?: ClassroomPostAudience
  text: string
  /** A capsule's own title, when the entry is one. */
  capsuleTitle?: string
  /** Set when the same content also lives on the author's profile. */
  postUri?: string
  postCid?: string
  /**
   * Set when the entry is the room announcing a change to itself.
   *
   * The server writes it when the level moves; it is not something an author
   * chooses, which is why it is not on any publish path.
   */
  announcement?: 'level'
  /** The level the announcement names, when the entry is one. */
  announcedLevel?: string
  /** The year the announcement names, when the level it names has one. */
  announcedGrade?: string
  /**
   * Set when this is a capsule the classroom alone holds — one that was made
   * for the room and never published as a post (see `isClassroomExclusive`).
   */
  capsuleUri?: string
  capsuleCid?: string
  /** A book's own title, when the entry is one. */
  flashbookTitle?: string
  /**
   * The book record, when the entry is a book.
   *
   * A book is always two records — a post and the record beside it — so this
   * comes with `postUri` or not at all (see `ClassroomContent`).
   */
  flashbookUri?: string
  flashbookCid?: string
  /** A module's own title, when the entry is one. */
  moduleTitle?: string
  /**
   * The module record, when the entry is a module.
   *
   * Like a book, a module is always two records — a post and the playlist beside
   * it — so this comes with `postUri` or not at all (see `ClassroomContent`).
   */
  moduleUri?: string
  moduleCid?: string
  author: {
    did: string
    handle: string
    displayName?: string
    avatar?: string
  }
}

/**
 * Whether a piece of work belongs to this classroom and nowhere else.
 *
 * A capsule or a book made *in* a room is marked as the room's on the records it
 * publishes (see `CLASSROOM_ONLY_POST`), and a capsule made before that mark
 * existed has no post at all. Either way it is not work the world can reach: this
 * names the entry that the room keeps to itself.
 *
 * It reads *any* record address rather than the capsule's alone, because the rule
 * is about where the work lives and not about what kind of work it is — a book
 * made for a room is the room's own by exactly the same argument, and a check
 * that forgot it would put the *Classroom only* badge on every capsule and none
 * of the books. A module for a room is the third case of the same rule, and it is
 * carried by the same line.
 */
export function isClassroomExclusive(content: ClassroomContent): boolean {
  return (
    !!(content.capsuleUri || content.flashbookUri || content.moduleUri) &&
    content.visibility === 'members'
  )
}

/**
 * The mark a *post* carries when it was written for one classroom.
 *
 * A capsule published into a classroom is a post like any other — a post is what
 * makes work reactable, commentable and reachable — and the only thing that makes
 * it the room's own is that it says so on the record. It has to be said on the
 * record and nowhere else, because the record is the only thing that travels: a
 * client can decide what to show from the record itself, and a backend can
 * enforce the same rule when classrooms stop living on one device.
 *
 * What this mark is *not* is a privacy guarantee — AT Protocol has no way to
 * make a post private, which is why a capsule *or a book* for one room is still a
 * post that another client could find. What it is: the app never puts it in a
 * feed, on a profile or in a capsule grid, and neither does anything built on
 * this fork. Nothing in the rule depends on what kind of work the post carries,
 * which is why a book made for a room is hidden by the same line of code.
 */
export const CLASSROOM_ONLY_POST = 'classroom'

/** Whether a post says it was written for one classroom. */
export function isClassroomOnlyPost(post: {record?: unknown}): boolean {
  const record = post?.record as Record<string, unknown> | undefined
  return record?.visibility === CLASSROOM_ONLY_POST
}

/**
 * The room entry's `visibility` for work added from the author's library.
 *
 * Published work is the author's public work, so it is `public` in the room by
 * default (spec 11.4); only work that is itself classroom-only (its record says
 * `visibility: 'classroom'`) is added as the room's members-only entry.
 */
export function libraryEntryVisibility(item: {
  visibility?: string
}): 'public' | 'members' {
  return isClassroomOnlyPost({record: item}) ? 'members' : 'public'
}

/**
 * What a post says about the classroom it was written in.
 *
 * The second mark a post can carry, and a different fact from
 * `CLASSROOM_ONLY_POST`: that one says *the room is the only place this lives*,
 * while this one says *this was written in a room* — and a post written in a
 * public room is meant to travel. It rides on the record rather than in a local
 * index because the record is the only thing that travels: a feed on another
 * device can draw the room's own byline from it, and a client that has never
 * heard of this classroom can still tell that the post is one room's post rather
 * than its author's.
 *
 * The cover is deliberately *not* here. A classroom's cover is device-local
 * bytes (`#/lib/media/coverPicture`), and a base64 picture inside every post
 * record would be a picture carried forever to say a thing the reader can be
 * shown a gradient for instead.
 */
export const classroomPostMarkSchema = z.object({
  /** The room's own id, as its device names it. */
  id: z.string(),
  name: z.string(),
  /** The room's own slug, printed beside its name. */
  handle: z.string(),
  /**
   * The school's handle: the half of a room's address that names the repo its
   * page lives in, and the only half that opens the room on a device that has
   * never seen it. Optional because posts written before the school rode on the
   * mark still read — those carry the room's slug alone, and the byline falls
   * back to it (`ClassroomPostByline`).
   */
  ownerHandle: z.string().optional(),
  visibility: classroomVisibilitySchema,
  /**
   * Who the author wrote this for, when they were asked (`classroomPostAudienceSchema`).
   *
   * Optional because it arrived after the mark did: every post written before the
   * question existed was written for whoever its room is for, which is exactly
   * what an absent audience still reads as (`postAudience`). A post with an
   * answer of its own that was later lost to another client must not become the
   * world's, so the mark's `visibility` (the author's public-or-room choice) is
   * the ceiling and can only ever narrow what the audience says
   * (`isClassroomPostVisibleTo`).
   *
   * Written as the seats the author named, and written only when they named some:
   * a post for everybody carries no audience at all, which is the same shape as
   * every post written before the question existed — one answer for one meaning,
   * rather than an empty list that has to be told apart from a missing one.
   */
  audience: classroomPostAudienceSchema.optional(),
})
export type ClassroomPostMark = z.infer<typeof classroomPostMarkSchema>

/**
 * The room a post says it was written in, when it says so at all.
 *
 * Read defensively: the record arrives from the network, so anything that is not
 * a well-formed mark is read as no mark rather than trusted far enough to break
 * the feed drawing it.
 */
export function readClassroomPostMark(post: {
  record?: unknown
}): ClassroomPostMark | null {
  const record = post?.record as Record<string, unknown> | undefined
  const parsed = classroomPostMarkSchema.safeParse(record?.classroom)
  return parsed.success ? parsed.data : null
}

/**
 * The room a post names, as the room is *now*, when this device holds it.
 *
 * A post's record carries a snapshot of the room it was written in — its name
 * and its address — because a record is immutable and the mark is the only part
 * of it that travels (`readClassroomPostMark`). Renaming a room would otherwise
 * leave its old name on every post it ever made, which is not what renaming is
 * for. So the room answers for its name and address wherever the device has it.
 *
 * The mark's `visibility` and `audience` are kept as written: they are the
 * author's choice for this post (spec 11.3), not the room's privacy, so a Public
 * post in a private room stays public.
 */
export function currentClassroomMark(
  mark: ClassroomPostMark,
  classroom:
    | {
        name: string
        handle: string
        ownerHandle?: string
      }
    | undefined,
): ClassroomPostMark {
  if (!classroom) return mark
  return {
    ...mark,
    name: classroom.name,
    /*
     * The school moves with the room: a post written before the mark carried it
     * still knows it here, on the device that holds the room, which is what
     * keeps an old post's byline openable from anywhere.
     */
    ownerHandle: classroom.ownerHandle ?? mark.ownerHandle,
    handle: classroom.handle,
  }
}

/**
 * Whether a post written in a room is one not everybody may read.
 *
 * Two answers make it so, and either is enough: the author kept the post to the
 * room (the mark's `visibility` is `private` - the author's choice, spec 11.3,
 * not the room's privacy, so a Public post in a private room is public); or the
 * author addressed the post to less than everyone. It is asked by the things
 * that would otherwise *spread* a post - the share menu, the copy-link item -
 * because a link to a room-only post is a link to work somebody decided against
 * publishing.
 */
export function isMembersOnlyClassroomPost(mark: ClassroomPostMark): boolean {
  return mark.visibility === 'private' || !mayAnyoneRead(postAudience(mark))
}

/**
 * Whether a reader may see what a room's post says.
 *
 * Two questions, and the post is readable only when both say yes. Who the author
 * addressed it to (`audienceIncludes`): a post addressed to the teachers is the
 * teachers'. And whether the author kept it to the room: the mark's `visibility`
 * is the author's choice when posting (spec 11.3), not the room's privacy, so a
 * `private` mark is for members only and a `public` one is for anybody.
 *
 * The seat a reader holds is the server's answer (`getViewerRoles`, see
 * `useClassroomPostRole`), which is why the answer is asked with it rather than
 * derived from the post alone: a reader with no seat in the room is a stranger.
 */
export function isClassroomPostVisibleTo(
  mark: ClassroomPostMark,
  viewer: {role: MemberRole | null},
): boolean {
  if (!audienceIncludes(postAudience(mark), viewer)) return false
  return mark.visibility === 'public' || viewer.role !== null
}

/**
 * The room's own words, as a post record carries them.
 *
 * Only the facts a reader needs to draw the post as a room's: what the room is
 * called, what it is called by, whether a stranger may read this - and the
 * school, because the address the byline opens is the school's half of it. The
 * room's id is written down too, so the post can be matched back to the room.
 *
 * `visibility` is the author's choice (spec 11.3): a post the author made
 * *Public* is public whatever the room, and a room-only post keeps the room's
 * members meaning (`private`). Without a choice the room's own visibility is
 * written, as every record before the choice existed was.
 */
export function classroomPostMark(
  classroom: {
    id: string
    name: string
    handle: string
    ownerHandle: string
    visibility: ClassroomVisibility
  },
  {
    audience,
    visibility,
  }: {
    audience?: ClassroomPostAudience
    visibility?: 'public' | 'members'
  } = {},
) {
  return {
    id: classroom.id,
    name: classroom.name,
    handle: classroom.handle,
    /*
     * The repo the room's page lives in, named by a handle: without it a mark
     * read on a device that has never seen the room has a name and no address.
     */
    ownerHandle: classroom.ownerHandle,
    visibility: (visibility === undefined
      ? classroom.visibility
      : visibility === 'public'
        ? 'public'
        : 'private') as ClassroomVisibility,
    /*
     * Only the seats that were named, and only when some were: *everybody* is the
     * absence of an answer rather than an empty list in the record.
     */
    ...(audience && !mayAnyoneRead(audience) ? {audience} : null),
  }
}

/**
 * Whether a feed may not show this post, for a reader holding these seats.
 *
 * Work written *for* one room (`isClassroomOnlyPost`) is no feed's. A post
 * written *in* a room is shown by the rule it is drawn by
 * (`isClassroomPostVisibleTo`), with the reader's seat taken from `roles` - the
 * server's answer to which rooms the viewer is in (`getViewerRoles`). No entry
 * is no seat.
 */
export function shouldDropFromFeed(
  post: {record?: unknown},
  roles: ReadonlyMap<string, MemberRole>,
): boolean {
  if (isClassroomOnlyPost(post)) return true
  const mark = readClassroomPostMark(post)
  if (!mark) return false
  return !isClassroomPostVisibleTo(mark, {role: roles.get(mark.id) ?? null})
}

/**
 * What a room's own record answers when it is asked whether the room is still
 * there (`useRoomRecord` in `ClassroomPostByline` spells the answers out of the
 * read itself).
 */
export type ClassroomRecordAnswer =
  /**
   * No address to ask at all: the mark names no school, and this device holds no
   * such room. Not the same as a room of this device's own that has never been
   * published — that one is *asked* (the address is the held room's) and answers
   * `unknown`.
   */
  | 'idle'
  /** The question has been asked and has not been answered yet. */
  | 'checking'
  /** The room's record is there: it is still a classroom. */
  | 'here'
  /** A tombstone: the room was ended. */
  | 'gone'
  /** No record at all: never published, which is not the same as ended. */
  | 'unknown'

/**
 * The room a post says it was written in, as it should be read *now*.
 *
 * A mark is a snapshot written into somebody's post forever, so *is this a
 * classroom's post?* has to be asked again at every reading. A post drawn as its
 * room's would be a post pointing at a place the reader cannot go, and a room
 * that was ended is not a place at all — so for a **public** room the answer is
 * *no mark*: the post becomes an ordinary post again, the author's own, drawn
 * with the avatar and byline every other post gets and with no cover, no room
 * name and no sign that a classroom was ever part of it.
 *
 * A **private** room's post is the one exception, and it is kept for the author's
 * sake rather than the room's: *private* was a promise to the people in it, and a
 * room ending does not make what was written in it public. Its mark stays, so the
 * lock survives and the card the lock draws is what drops the room's identity.
 *
 * The answer comes from the room's own record, and only from a tombstone:
 * `gone` is what the room's own device wrote when it ended the room, so no other
 * *answer* — not a room the device merely holds a copy of, not a record that is
 * missing, not a read that failed — may take a room off a post. That is what
 * makes the reply the same on every device: the copy one device happens to still
 * hold cannot keep a deleted room's name on a post somewhere else.
 *
 * The one thing that is not an answer is the moment before there is one, and a
 * post everybody may read does not wear the room's design while the room is being
 * asked about (`checking`): a deleted room's post would otherwise be dressed as
 * the room's for as long as the question took, on every page load, and a reader
 * shown that first has been told something that is not true. So `checking` reads
 * as *not the room's* for those posts — and as nothing at all for a members-only
 * one, whose mark has to stay whatever the answer is, because the mark is what
 * the locked card is drawn from.
 *
 * `idle` reads the same way, and it is the reading that never changes: there is
 * nowhere to ask at all, because the mark names no school and this device holds no
 * such room (`ClassroomRecordAnswer`). That is not *we could not ask* — `unknown`
 * is, and it leaves the mark where it is — it is *there is nowhere to ask*, and a
 * post whose room can never be asked about may not wear the room's design for the
 * very reason a post being asked about may not: the design is a claim about a
 * place, and the mark is only a snapshot of a room that may have been ended the day
 * after it was written. Kept, it would be the one drawing no tombstone could ever
 * take back — a deleted room's name, cover and lock on somebody's post, on every
 * device that does not hold the room, for good — and a byline whose own link
 * cannot land, because the room's slug alone (`classroomHref`) opens only the
 * devices that already have the room. It is what the marks an older build wrote
 * read as (`ownerHandle` arrived after them), and a mark the school rode on is
 * asked like every other.
 */
export function liveClassroomPostMark(
  mark: ClassroomPostMark | null,
  context: {
    record: {status: ClassroomRecordAnswer}
  },
): ClassroomPostMark | null {
  if (!mark) return null
  /*
   * *Everyone may read this* is the one post whose room can be forgotten, and it
   * takes both halves to be that post: a public room, and an audience that is
   * everybody. A post the author addressed to the room's members is not made
   * public by the room ending — the same promise a private room's post keeps — so
   * its mark stays and its lock stays with it.
   */
  const everyoneMayReadIt =
    mark.visibility === 'public' && mayAnyoneRead(postAudience(mark))
  if (!everyoneMayReadIt) return mark
  /*
   * Three readings leave the post drawn as its author's own rather than the
   * room's, and they are three ways of *not knowing the room*.
   *
   * `checking` is the moment a page loads. The answer takes a read and the read
   * takes a moment, and the room's design — its cover where the avatar goes, its
   * name where the display name goes — is a claim about a place. A post from a
   * room that was ended would wear that claim for as long as the read took and be
   * handed back a beat later as an ordinary post, and the reader has no way to
   * know which of the two they were shown first. So the room's design is put on a
   * post only once the room is known to be there, and an ordinary drawing is never
   * a lie about a post everybody may read: it is exactly what the post turns out
   * to be when the room is gone.
   *
   * `gone` is the answer itself: the room's own device wrote that the room was
   * ended.
   *
   * `idle` is the answer that never comes, and it is drawn the same way on
   * purpose: the mark names no school and this device holds no such room, so there
   * is nowhere to ask at all. Kept, the room's cover, name and lock would be
   * permanent on every device that does not hold the room, and no school could ever
   * take them back, because there is no record for it to write on.
   *
   * `unknown` is the one reading that keeps the mark, and just as deliberately: it
   * is a question that *was* asked and came back with no record — a read that
   * failed, or a room whose page its own device has not written yet — and the app
   * does not take a room off a post over an answer it never got.
   */
  const status = context.record.status
  if (status === 'checking' || status === 'idle' || status === 'gone') {
    return null
  }
  return mark
}

/**
 * The audience each author last chose to keep, per classroom.
 *
 * The composer asks *who is this for* on every post in a room, and the answer
 * is almost always the same as last time — so it can be kept, and it is kept
 * here rather than assumed. Kept *per account* because a shared device may hold
 * two people's answers, and one of them choosing to write to the room's teachers
 * must not silently make that the other one's default.
 *
 * Kept only when the author asks for it (`PostAudienceDialog`'s "save this
 * audience for next time"): a remembered audience is a standing decision about
 * work not yet written, and the person who chose *students only* for one exam
 * paper has not said anything about the announcement after it. Nothing is
 * written here otherwise, which is what makes the checkbox the whole of the
 * promise — untick it and this post, and only this post, is narrowed.
 *
 * An answer is a list of seats like every other audience (`ClassroomPostAudience`),
 * and one remembered by a build that asked the question with a single choice is
 * read as what it meant when it is read (`readPostAudience`), so the two shapes
 * never both reach the composer.
 */
export const classroomAudienceStateSchema = z.object({
  version: z.number(),
  /** Author did → classroom id → the audience they kept for it. */
  byDid: z.record(
    z.string(),
    z.record(z.string(), classroomPostAudienceSchema),
  ),
})
export type ClassroomAudienceState = z.infer<
  typeof classroomAudienceStateSchema
>
export const CLASSROOM_AUDIENCE_STATE_VERSION = 1

/**
 * Levels where parents are part of the room by right.
 *
 * Parents following their children into the room is the point of the youngest
 * classrooms, so a creator cannot turn parents away from a Kindergarten,
 * Primary School or Middle School classroom — at creation or later. Every other
 * level leaves the choice to the school.
 */
export const PARENT_GUARANTEED_LEVELS = [
  'Kindergarten',
  'Primary School',
  'Middle School',
] as const

/**
 * The level where keeping parents out is common enough to suggest it.
 *
 * Optional like High School and University, with a hint: parents of teenagers
 * are the ones a school most often leaves outside the room.
 */
export const SECONDARY_SCHOOL_LEVEL = 'Secondary School'

/**
 * The second name for the secondary years.
 *
 * A learner names their stage `High School` and the record's shared level is
 * `Secondary School`; both are the years between middle school and university.
 * They map to the same learner stage ({@link LEARNER_STAGES_FOR_LEVEL}) and
 * carry the same years (`CLASSROOM_LEVEL_GRADES`), because the two names exist
 * only because a school says one and a learner says the other — neither is
 * wrong, and a room under either is the same room.
 */
export const HIGH_SCHOOL_LEVEL = 'High School'

/** The levels everyone can filter by. */
export const STANDARD_LEVELS = [
  'Kindergarten',
  'Primary School',
  'Middle School',
  'Secondary School',
  'High School',
  'University',
  'Other',
] as const

/** The interests a classroom can be filed under, for when the list is long. */
export const CLASSROOM_INTERESTS = [
  'STEM',
  'Science',
  'Mathematics',
  'Languages',
  'Arts',
  'Music',
  'Sports',
  'Coding',
  'Reading',
  'History',
] as const

export interface ClassroomCounts {
  /** The school that runs the room. Never counted as a teacher. */
  admins: number
  teachers: number
  students: number
  parents: number
}

/** Whether a level guarantees parents a way into its classrooms. */
export function levelGuaranteesParents(level: string): boolean {
  return (PARENT_GUARANTEED_LEVELS as readonly string[]).includes(level)
}

/**
 * Whether a classroom names a level at all.
 *
 * A classroom with no level is the open one: every learner may join it whatever
 * year they are in, because there is no year it could disagree with. The field
 * stays a string — an empty one — rather than becoming optional, so every
 * classroom ever written already has the shape the new rule reads.
 */
export function classroomHasLevel(classroom: {level: string}): boolean {
  return classroom.level.trim().length > 0
}

/**
 * The level, as the room says it: the school's own name where it has one.
 *
 * Every surface that prints a room's level prints this rather than the record's
 * own name, because a school that called its room `Quran memorisation` should
 * not be introduced as `Other` — the record keeps the shared level for the
 * machinery (the gate, the filters, a level change), and the room keeps its
 * name for the people.
 */
export function classroomLevelName(classroom: {
  level: string
  levelLabel?: string
}): string {
  const own = classroom.levelLabel?.trim()
  return own || classroom.level
}

/**
 * Which learner stages a classroom level is for.
 *
 * The learner names their stage during onboarding in the app's own words
 * (`Primary school`, `High School`) while a classroom's level is one of
 * `STANDARD_LEVELS`; this is the translation between the two. A stage that maps
 * to no level — a self-learner, a lifelong learner — is in no list and is
exempted rather than blocked: there is nothing to compare, and refusing on a
question a learner never answered would hide every school room from them.
 *
 * `High School` (the learner's own secondary stage) is the one stage that is for
 * two classroom levels: a school may call its secondary years either name, and
 * both are the years that stage describes.
 */
const LEARNER_STAGES_FOR_LEVEL: Record<string, string[]> = {
  Kindergarten: ['Preschool / Kindergarten'],
  'Primary School': ['Primary school'],
  'Middle School': ['Middle School'],
  [SECONDARY_SCHOOL_LEVEL]: ['High School'],
  [HIGH_SCHOOL_LEVEL]: ['High School'],
  University: ['University'],
}

/**
 * Whether a learner may join this classroom, and what the door found when they may not.
 *
 * The rule the app promises a school: a classroom pinned to a level and a year
 * takes the learners of that level and year, and a learner whose own level is
 * different is told so rather than quietly let in. The room is also a *country's*
 * school, so a learner in another country is not its audience either — unless the
 * school made it international.
 *
 * Four answers, and they are not the same thing:
 *
 *  - **a learner in another country** (`'country'`) — asked first, because it is
 *    the one fact no answer of the learner's own can change. It belongs to the
 *    learner alone (a school invites its teachers, a parent follows their child)
 *    and it is asked of **every** room, including one with no level: a room with
 *    no level is open to every year, not to every country;
 *  - **a different level or year** (`'level'` / `'grade'`) — the learner *said*,
 *    and what they said is another class;
 *  - **a level the app was never told** (`'unknown'`) — the account is a
 *    **learner**, the room teaches a level, and the learner's own stage (or the
 *    year the room names) has never been answered anywhere the app can read.
 *    Since 2026-10-04 this is a shut door rather than an open one: the level is
 *    what the room is *for*, and letting a learner through on *silence* is how a
 *    learner with no level at all could walk into any classroom — which is what
 *    the school saw. The answer points at the picker, so the one thing standing
 *    between them and the room is a two-tap answer.
 *  - **nothing to ask** — a room with no level takes everybody, and an account
 *    that is not a learner (a creator, a parent, a school) has no class year to
 *    check: teachers and parents are let in by the room's own rules, not by a
 *    year. The `'unknown'` rule is deliberately the *learner's* alone.
 */
export function learnerMayJoinClassroom(
  classroom: {
    level: string
    grade?: string
    country?: string
    international?: boolean
  },
  learner: {
    learningStage?: string | null
    gradeLevel?: string | null
    accountType?: string | null
    country?: string | null
  },
): {
  allowed: boolean
  reason: 'level' | 'grade' | 'unknown' | 'country' | null
} {
  /*
   * The country is asked first, and it is asked of **every** room.
   *
   * A classroom with no level is open to every learner *of its country* — it is
   * the year it declines to name, not the country it sits in — so this rule
   * cannot live under the level's own early return. Where a person lives is
   * also the one fact no answer of their own can change, which is why it is
   * stated before the questions they can act on.
   */
  if (learner.accountType === 'learner' && classroom.international !== true) {
    const home = (classroom.country ?? '').trim().toUpperCase()
    const mine = (learner.country ?? '').trim().toUpperCase()
    if (home && mine && home !== mine) {
      return {allowed: false, reason: 'country'}
    }
  }

  if (!classroomHasLevel(classroom)) return {allowed: true, reason: null}
  const stages = LEARNER_STAGES_FOR_LEVEL[classroom.level]
  const checksStage = !!stages && stages.length > 0
  if (
    learner.learningStage &&
    checksStage &&
    !stages.includes(learner.learningStage)
  ) {
    return {allowed: false, reason: 'level'}
  }

  /*
   * Silence is a question, not a pass — for a learner, at a room that teaches a
   * level or names a year.
   */
  const isLearner = learner.accountType === 'learner'
  if (isLearner && checksStage && !learner.learningStage) {
    return {allowed: false, reason: 'unknown'}
  }
  const checksYear =
    !!classroom.grade && !!CLASSROOM_LEVEL_GRADES[classroom.level]
  if (isLearner && checksYear && !learner.gradeLevel) {
    return {allowed: false, reason: 'unknown'}
  }

  if (classroom.grade && learner.gradeLevel && checksYear) {
    if (!levelAgreesWithGrade(classroom.grade, learner.gradeLevel)) {
      return {allowed: false, reason: 'grade'}
    }
  }

  return {allowed: true, reason: null}
}

/**
 * Whether a classroom is one this reader may be shown.
 *
 * A public room is shown to anybody — it is the kind that can be found. A
 * private room is the kind that cannot, so it is shown to the school that runs
 * it and to the people it has already let in, and to nobody else. The rule is
 * read by the list (a profile's Classrooms tab draws what its owner runs), and a
 * private room a stranger could see there would be a private room that is
 * findable — which is the one thing the word promises it is not.
 */
export function classroomVisibleTo(
  classroom: Classroom,
  did: string | undefined,
): boolean {
  if (classroom.visibility !== 'private') return true
  if (!did) return false
  return classroom.ownerDid === did || findMember(classroom, did) !== undefined
}

/**
 * How long a classroom's level must stand before it may be changed again.
 *
 * A month, because the level is what the room *is*: everyone in it was told to
 * be there because of it, and the people who move are the ones it no longer
 * matches. A room that could be re-levelled every week would move its members
 * every week.
 */
export const LEVEL_CHANGE_COOLDOWN_DAYS = 30

const DAY_MS = 24 * 60 * 60 * 1000

/**
 * When the classroom's level may next change, or `null` when it may change now.
 *
 * Only the level and its year count: the name, the bio, the cover and who can
 * find the room are not what its members are here for, and an edit that fixes a
 * typo is not a level change. The stamp is written when the level moves, so a
 * room made by mistake can be corrected the same day and then stands for a month
 * (the server enforces the same wait).
 *
 * An unreadable stamp is read as *no cooldown* for the same reason an absent one
 * is: refusing to change a level because a date could not be parsed would make a
 * corrupt field into a locked classroom.
 */
export function levelChangeLockedUntil(
  classroom: {levelChangedAt?: string},
  now: Date = new Date(),
): Date | null {
  const changedAt = classroom.levelChangedAt
  if (!changedAt) return null
  const changedMs = Date.parse(changedAt)
  if (Number.isNaN(changedMs)) return null
  const until = new Date(changedMs + LEVEL_CHANGE_COOLDOWN_DAYS * DAY_MS)
  return until.getTime() > now.getTime() ? until : null
}

/**
 * Whether a parent may join this classroom.
 *
 * Absent means yes — the default every classroom starts with, and the only
 * value a record written before the rule can carry. Only an explicit `false`,
 * chosen by the creator where the level allows it and no parent is seated yet,
 * keeps parents out.
 */
export function parentsMayJoin(classroom: Classroom): boolean {
  return classroom.parentsCanJoin !== false
}

/**
 * Who may answer this kind of ask, in the room's own terms.
 *
 * Read through one function rather than off the fields, because absent is a
 * meaningful answer here and every caller would otherwise have to remember it.
 */
export function classroomApproversFor(
  classroom: {
    studentApprovers?: ClassroomApprovers
    parentApprovers?: ClassroomApprovers
  },
  role: 'student' | 'parent',
): ClassroomApprovers {
  const chosen =
    role === 'student' ? classroom.studentApprovers : classroom.parentApprovers
  return chosen ?? ALL_TEACHERS
}

/**
 * Whether this seat is a moderator of the room.
 *
 * The school is never one: it already has every power a moderator has and one
 * more, and listing it as its own moderator would be a seat described twice.
 */
export function isClassroomModerator(
  classroom: {ownerDid: string; moderators?: string[]},
  did: string | undefined,
): boolean {
  if (!did || did === classroom.ownerDid) return false
  return (classroom.moderators ?? []).includes(did)
}

/** Whether this person may run the room: the school, or a moderator it named. */
export function canManageClassroom(
  classroom: Classroom,
  did: string | undefined,
): boolean {
  if (!did) return false
  return did === classroom.ownerDid || isClassroomModerator(classroom, did)
}

/**
 * Whether this person may delete the room.
 *
 * Only the school ever may. A moderator is trusted with the room's rules, not
 * with the room's existence.
 */
export function canDeleteClassroom(
  classroom: {ownerDid: string},
  did: string | undefined,
): boolean {
  return !!did && did === classroom.ownerDid
}

/**
 * Whether this person may answer a request for a seat.
 *
 * The school always may, a moderator it named may, and everybody else is the
 * room's own setting: the teachers of the room when it says `'all-teachers'`,
 * and only the seats it names otherwise. A *teacher* asking to join is the one
 * case the setting does not cover — a teacher walks in beside the school that
 * hires them, so the answer is the school's own (and its moderators').
 */
export function canApproveJoinRequest(
  classroom: Classroom,
  viewer: {did?: string} | undefined,
  role: JoinableRole,
): boolean {
  const did = viewer?.did
  if (!did) return false
  if (did === classroom.ownerDid || isClassroomModerator(classroom, did)) {
    return true
  }
  if (role === 'teacher') return false
  const approvers = classroomApproversFor(classroom, role)
  if (approvers === ALL_TEACHERS) {
    return findMember(classroom, did)?.role === 'teacher'
  }
  return approvers.includes(did)
}

/**
 * Whether a classroom has anything to say about parents at all.
 *
 * A room that keeps parents out has no parents to count, and a `0 parents` line
 * on a page that does not take parents is a question the room has already
 * answered — it is the count of a seat nobody can hold. So the count is spoken
 * only where parents may join, and the one case where it survives that is a room
 * that says parents cannot join while a parent is somehow seated in it: a seat
 * that exists is a seat that is counted, whatever the rule says.
 */
export function showsParentsCount(classroom: Classroom): boolean {
  return parentsMayJoin(classroom) || countMembers(classroom).parents > 0
}

export function countMembers(
  classroom: Classroom,
  options?: {
    /**
     * Whether seats nobody holds an account for are counted. Default: they are.
     *
     * A student joining brings a parent's seat with them, and when the app knows of
     * no parent account the seat still exists and still says whose parent it is
     * (`placeholder`). On the device that holds the room that seat **is** a seat —
     * the room has a parent in it, and the school reads its own room that way. The
     * room's *published* counts are a different audience: a stranger reading a link
     * is being told how many people are in the room, and counting a seat with no
     * account behind it as a person inflates a number nobody can check. So a
     * public count asks for real seats only.
     */
    placeholders?: boolean
  },
): ClassroomCounts {
  const countPlaceholders = options?.placeholders ?? true
  let admins = 0
  let teachers = 0
  let students = 0
  let parents = 0
  for (const m of classroom.members) {
    if (!countPlaceholders && m.placeholder) continue
    if (m.role === 'admin') admins++
    else if (m.role === 'teacher') teachers++
    else if (m.role === 'student') students++
    else parents++
  }
  return {admins, teachers, students, parents}
}

/**
 * The counts a page should print for a room.
 *
 * A room this device made or joined counts its own members, and that is the whole
 * of it (`countMembers`). A room this device read from a published record holds
 * the school and nobody else, with the school's own count of its seats beside it
 * (`publishedCounts`) — and printing `0 students` about a class of forty is not
 * modesty, it is a wrong number. So the published counts stand in for the seats
 * that are not here, and never *hide* a seat that is: a person this device has
 * seated is counted whether the record predicted them or not.
 */
export function roomCounts(classroom: Classroom): ClassroomCounts {
  const local = countMembers(classroom)
  const published = classroom.publishedCounts
  if (!published) return local
  return {
    admins: local.admins,
    teachers: Math.max(local.teachers, published.teachers),
    students: Math.max(local.students, published.students),
    parents: Math.max(local.parents, published.parents),
  }
}

/**
 * Whether a classroom's owner is behind a member record at all.
 *
 * The school is a member of its own classroom — it has to be, because that
 * record is where the seat it holds is written — but it is a member of a
 * different kind, and everything that lists or counts members treats it apart.
 */
export function isAdminMember(
  classroom: Classroom,
  member: ClassroomMember,
): boolean {
  return member.role === 'admin' || member.did === classroom.ownerDid
}

/** Whether a piece of content is for the room or for everyone. */
export function isContentVisibleTo(
  content: ClassroomContent,
  viewer: {role: MemberRole | null},
): boolean {
  // With no audience of its own, an entry names nobody narrower than its
  // visibility; the members-only rule for `members` content is applied below.
  const audience = content.audience ?? []
  if (!audienceIncludes(audience, viewer)) return false
  return content.visibility === 'public' || viewer.role !== null
}

/**
 * A room's address when its name gives none.
 *
 * A handle is the readable half of every link a school hands out, and a room
 * whose name is not written in Latin letters — an Arabic name, an emoji — has no
 * slug to take from it (`slugify` transliterates what it can). The room's own id
 * is the answer then, because the id is the one name a room is guaranteed to
 * have: `room-<first letters of it>` is unique, stable, and short.
 *
 * The word is `room`, never `classroom`. The path already says *classroom*, and
 * a handle that says it too is how a link ends up reading
 * `/classroom/classroom/…`.
 */
export function fallbackHandle(id: string): string {
  return `room-${id.slice(0, 6)}`
}

/**
 * Whether a piece of work was written while the room taught something else.
 *
 * This is the *whole* of the archive rule, and it is deliberately a question
 * about the present rather than a flag written on the work: the work carries the
 * level it was written at (`classroomLevel`), the room carries what it teaches
 * now, and the two answers say everything. A room that moves away from a level
 * thereby archives the work from it, and a room that moves *back* to it — which
 * the cooldown allows and a school that made a mistake will do — brings that work
 * back with it, without anything having to be written, found and rewritten.
 *
 * Work with no recorded level is never archived. Every entry written before the
 * level was recorded is read as belonging to the room as it stands, because
 * hiding work nobody wrote a level for would hide a room's own history from the
 * school that made it.
 */
export function contentFromAnotherLevel(
  content: {classroomLevel?: string; classroomGrade?: string},
  classroom: {level: string; grade?: string},
): boolean {
  if (content.classroomLevel === undefined || content.classroomLevel === '') {
    return false
  }
  if (content.classroomLevel !== classroom.level) return true
  return (content.classroomGrade ?? '') !== (classroom.grade ?? '')
}

/** A period the room has left behind, and the work written during it. */
export interface ClassroomLevelArchive<T> {
  /** Stable across renders: the level and year the work was written at. */
  key: string
  /** The level that period was, and its year when it had one. */
  level: string
  grade?: string
  /** Where the room went when it left that level, when the move is recorded. */
  to?: string
  toGrade?: string
  /** When it left, when the move is recorded. */
  movedAt?: string
  /** That period's work, in the order it was handed in. */
  items: T[]
}

/**
 * Who a level announcement is for.
 *
 * The room tells the people it concerns, and nobody else: a member who already
 * studies what the room now teaches has nothing to answer, and an announcement
 * shown to them is a card they have to dismiss for no reason. Three things decide
 * it, and each is a fact the app has:
 *
 * - **The room's own school is not told.** It moved the level on purpose.
 * - **The seat has to predate the change** — the members who were in the room
 *   before it moved are the ones the sentence *this is not what you signed up
 *   for* is addressed to. Somebody who joined afterwards joined the room as it
 *   is now, which is the one thing this announcement is not.
 * - **A learner is told only when the app can see the disagreement.** A seat
 *   that studies something else is told; a seat whose level the app has never
 *   been given is not, because *unknown* is not *different* — the same rule every
 *   door follows (`learnerMayJoinClassroom`). A teacher or a parent is told, and
 *   is offered the only answer that seat has: keep the room, or leave it.
 */
export function announcementIsForMember(
  content: ClassroomContent,
  classroom: Classroom,
  viewer:
    | {
        did?: string
        learningStage?: string | null
        gradeLevel?: string | null
      }
    | undefined,
): boolean {
  if (content.announcement !== 'level') return false
  const member = findMember(classroom, viewer?.did)
  if (!member || member.role === 'admin') return false
  if (content.acknowledged?.includes(member.did)) return false
  /*
   * The seat was there before the room moved. Timestamps are ISO strings, so the
   * comparison is a sort — and a seat with no readable join date stays, because
   * a member the app cannot date is not a member who arrived late.
   */
  if (member.joinedAt > content.createdAt) return false
  if (member.role !== 'student') return true
  return !learnerMayJoinClassroom(
    {
      level: content.announcedLevel ?? classroom.level,
      grade: content.announcedGrade,
    },
    {
      learningStage: viewer?.learningStage,
      gradeLevel: viewer?.gradeLevel,
    },
  ).allowed
}

/**
 * The room's left-behind levels, newest first, each with the work from it.
 *
 * Grouped by what each entry says about itself rather than by the move list, so
 * the list is right even where the history is incomplete: the moves say what the
 * room moved from and to (they are what the divider prints), and the entries say
 * which period they belong to. A level with entries and no recorded move still
 * gets its group — the divider then says the content's level and nothing about
 * where the room went, which is the truth of a device that did not see the move.
 */
export function levelArchiveGroups<T extends ClassroomContent>(
  classroom: Classroom,
  items: T[],
): ClassroomLevelArchive<T>[] {
  const groups = new Map<string, ClassroomLevelArchive<T>>()
  for (const item of items) {
    if (!contentFromAnotherLevel(item, classroom)) continue
    const level = item.classroomLevel!
    const grade = item.classroomGrade ?? undefined
    const key = grade ? `${level} · ${grade}` : level
    const group = groups.get(key)
    if (group) {
      group.items.push(item)
    } else {
      groups.set(key, {key, level, grade, items: [item]})
    }
  }
  const moves = classroom.levelMoves ?? []
  for (const group of groups.values()) {
    // The move that ended this period is the last one that left it.
    const move = [...moves]
      .reverse()
      .find(
        m =>
          m.from === group.level && (m.fromGrade ?? '') === (group.grade ?? ''),
      )
    const fallback = moves.filter(m => m.from === group.level).at(-1)
    const found = move ?? fallback
    if (found) {
      group.to = found.to
      group.toGrade = found.toGrade
      group.movedAt = found.at
    }
  }
  return [...groups.values()].sort((a, b) => {
    const left = a.movedAt ?? a.items[0]?.createdAt ?? ''
    const right = b.movedAt ?? b.items[0]?.createdAt ?? ''
    return right.localeCompare(left)
  })
}

/**
 * A classroom's path: the **school**, then the room's own handle.
 *
 * These are the two halves the server finds a room by (`getClassroom` with
 * `owner` and `handle`). Every way into a room goes through here
 * (`classroomLink` for the copyable address, `classroomMembersLink` for the
 * roster, the card and the post byline for taps).
 */
export function classroomRoute(classroom: {
  ownerHandle: string
  handle: string
}): string {
  return `/classroom/${classroom.ownerHandle}/${classroom.handle}`
}

/** The path to the classroom's member list, optionally on one role's tab. */
export function classroomMembersLink(
  classroom: Classroom,
  role?: JoinableRole,
): string {
  const base = `${classroomRoute(classroom)}/members`
  return role ? `${base}?role=${role}` : base
}

export function findMember(
  classroom: Classroom,
  did: string | undefined,
): ClassroomMember | undefined {
  if (!did) return undefined
  return classroom.members.find(m => m.did === did)
}

export function hasRequested(
  classroom: Classroom,
  did: string | undefined,
): boolean {
  if (!did) return false
  return classroom.requests.some(r => r.did === did)
}

/**
 * A classroom's address: the school's handle, then the room's handle - the two
 * halves the server finds the room by (`classroomRoute`).
 */
export function classroomLink(classroom: Classroom): string {
  return `https://taderok.com${classroomRoute(classroom)}`
}

/** A short, quotable code a school can read aloud. */
export function makeJoinCode(): string {
  const alphabet = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789'
  let out = ''
  for (let i = 0; i < 6; i++) {
    out += alphabet[Math.floor(Math.random() * alphabet.length)]
  }
  return out
}

/**
 * The meeting point an ask that crosses devices comes back to.
 *
 * A seat in the school's repo is filed under this string and carries nothing else
 * about who holds it, so the answer to *did they let me in?* is a record that has
 * to be *findable* by the person it is about and by nobody else: the token is
 * drawn at random on the asker's device, kept there, and sent with the ask, and
 * the school writes the seat at exactly that key. Randomness is the whole of the
 * privacy: 24 letters of it is not guessable, and the pattern is the one a record
 * key is allowed to be — lowercase letters and digits, nothing to escape.
 */
export function makeAskToken(): string {
  const alphabet = 'abcdefghijklmnopqrstuvwxyz0123456789'
  let out = ''
  for (let i = 0; i < 24; i++) {
    out += alphabet[Math.floor(Math.random() * alphabet.length)]
  }
  return out
}

export function makeId(): string {
  return (
    Date.now().toString(36) + Math.random().toString(36).slice(2, 10)
  ).toLowerCase()
}

/**
 * The Latin letters an Arabic letter is written as, for a handle.
 *
 * Only letters: a name is slugged for a *link*, and a link wants to be short and
 * recognizable rather than scholarly — `غرفة` becoming `ghrfa` is what a person
 * typing the address would try. Short vowels are not written in the script and
 * are not invented here.
 */
const ARABIC_LETTERS: Record<string, string> = {
  ا: 'a',
  أ: 'a',
  إ: 'a',
  آ: 'a',
  ى: 'a',
  ة: 'a',
  ء: '',
  ب: 'b',
  ت: 't',
  ث: 'th',
  ج: 'j',
  ح: 'h',
  خ: 'kh',
  د: 'd',
  ذ: 'dh',
  ر: 'r',
  ز: 'z',
  س: 's',
  ش: 'sh',
  ص: 's',
  ض: 'd',
  ط: 't',
  ظ: 'z',
  ع: 'a',
  غ: 'gh',
  ف: 'f',
  ق: 'q',
  ك: 'k',
  ل: 'l',
  م: 'm',
  ن: 'n',
  ه: 'h',
  و: 'w',
  ي: 'y',
  ئ: 'y',
  ؤ: 'w',
}

/** The marks a letter carries rather than being one, and the joining dash. */
const ARABIC_MARKS = /[\u0640\u064b-\u0652\u0670]/g

/**
 * A name, as a handle: lower case, Latin, one dash between words.
 *
 * A name written in another script is transliterated first rather than thrown
 * away, because the handle is what the link *reads* as — a school whose name is
 * Arabic should get `ghrfa-tjrybya`, not `classroom`. Scripts with no table here
 * (Chinese, emoji) leave nothing behind, and a caller that needs an address has
 * `fallbackHandle` for that.
 */
export function slugify(name: string): string {
  return name
    .replace(ARABIC_MARKS, '')
    .replace(/[\u0621-\u064a]/g, letter => ARABIC_LETTERS[letter] ?? letter)
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 40)
}

/**
 * Who is acting.
 *
 * `accountType` is the field onboarding writes for the account, and it decides
 * the seat a person takes: a learner is a student, a parent is a parent, and a
 * creator or an organisation (a school) is a teacher in someone else's room.
 * Nobody but the owner is ever an admin.
 */
export interface ClassroomActor {
  did: string
  handle: string
  displayName?: string
  avatar?: string
  accountType?: string | null
  /**
   * The learner's own stage and year, as onboarding recorded them.
   *
   * They travel with the actor for one rule only: a classroom pinned to a year
   * takes the learners of that year (`learnerMayJoinClassroom`). Both are
   * *absent* on the accounts that never said — a school, a parent, a learner
   * whose deployment does not hand the answer back yet — and absent is no
   * evidence of a disagreement, so no such account is ever turned away by it.
   */
  learningStage?: string | null
  gradeLevel?: string | null
  /**
   * The country the account's own onboarding recorded, as an ISO code.
   *
   * It travels with the actor for one rule, the same way the level does: a
   * classroom takes the learners of its own country (`learnerMayJoinClassroom`),
   * and a school whose country has never been read puts none on its rooms.
   * Absent is no evidence of a disagreement, so no account is refused for it.
   */
  country?: string | null
  /**
   * The parent account tied to a learner, when the app knows of one. Used so a
   * student joining a classroom brings their real parent rather than a seat.
   */
  linkedParent?: {
    did: string
    handle: string
    displayName?: string
    avatar?: string
  }
}

export interface ClassroomDraft {
  name: string
  description: string
  visibility: 'public' | 'private'
  level: string
  levelNameType: 'STANDARD' | 'SYSTEM'
  /**
   * The school's own name for its level, for the two levels that are not a
   * country's school years (`University`, `Other`). Empty means the shared name
   * (`classroomLevelName`).
   */
  levelLabel?: string
  /**
   * The country the room takes its learners from, taken from the school's own
   * account at creation. Absent when the account's country is unknown.
   */
  country?: string
  /**
   * Whether the room takes the learners of every country. The school's own
   * answer for a University or `Other` room; absent means its own country's
   * learners, which is what every classroom did before the field.
   */
  international?: boolean
  interests: string[]
  /**
   * Whether parents may join. Absent is the default — they may — and a level
   * that guarantees parents (Kindergarten, Primary and Middle School) turns
   * even an explicit `false` into yes.
   */
  parentsCanJoin?: boolean
  /** The cover's bytes, as `#/lib/media/coverPicture` reads them, or none. */
  cover?: string
  /** The uploaded cover blob's cid, sent to the server in place of the bytes. */
  coverCid?: string
  /**
   * The year inside the level. `''` and absent both mean *no year* — the whole
   * level — and a year that does not belong to the chosen level (or a level that
   * has no years at all) is dropped rather than written down wrong.
   */
  grade?: string
  owner: ClassroomActor
}

/** What a school can change about the classroom it runs. */
export interface ClassroomEdits {
  name?: string
  description?: string
  /**
   * The cover picture's bytes, or `''` to take the picture away.
   *
   * Bytes and not an address: a classroom is device-local, and so is its cover —
   * the picture is kept with the record and drawn from there
   * (`#/lib/media/coverPicture` says why an upload cannot stand in for it). Absent
   * means *leave it as it is* — the same shape `name` and `description` have, and the
   * reason the field is not simply `cover?: string` read as a value: a save that
   * changes the bio must not quietly drop the picture. An empty string is the one way
   * to *remove* a cover, which is what the editor's own *Remove Banner* presses, and
   * the store turns it into no cover at all rather than into bytes of nothing.
   */
  cover?: string
  /** The uploaded cover blob's cid, or `''` to remove the cover on the server. */
  coverCid?: string
  visibility?: 'public' | 'private'
  level?: string
  /**
   * The year inside the level. Absent means *leave it as it is*, and `''`
   * removes it; a year that does not belong to the level (after the level edit
   * itself, when there is one) is dropped — see `updateClassroom`.
   */
  grade?: string
  levelNameType?: 'STANDARD' | 'SYSTEM'
  /**
   * The school's own name for its level (`University`, `Other`). `''` removes
   * it, and an absent value leaves it as it is — the same shape every other
   * field of an edit has.
   */
  levelLabel?: string
  /**
   * Whether the room takes learners from every country. Absent means *leave it
   * as it is*; it is a decision about the room's audience, not about its level,
   * so it is free to change as the school's own answer changes.
   */
  international?: boolean
  /**
   * Whether parents may join, when it can be changed at all. A level that
   * guarantees parents forces it on, and a room with a parent already in it
   * keeps what it has — see `nextParentsCanJoin`.
   */
  parentsCanJoin?: boolean
  interests?: string[]
}
